import type { Course } from "../../content/course.ts";
import type { UnitDef } from "../../content/types.ts";
import { glyphFace } from "../../face.ts";
import type { ExplainStep } from "../../lesson.ts";
import { uid } from "../../rng.ts";
import { register } from "../registry.ts";
import type { BuildContext } from "../registry.ts";

export function makeExplain(unit: UnitDef, course: Course): ExplainStep {
	const forms = course
		.itemsOf(unit, "teaches")
		.filter((i) => i.kind === "form")
		.map((i) => course.views.form(i.id))
		.sort((a, b) => a.order - b.order);
	return {
		kind: "explain",
		rows: forms.map((f) => ({
			itemId: f.id,
			lead: f.lead === null ? null : glyphFace(course.views.tokenText(f.lead)),
			form: glyphFace(f.text),
			gloss: f.gloss,
		})),
		rule: course.t(`${unit.id}.explain.rule`),
		order: course.t(`${unit.id}.explain.order`),
		watch: course.t(`${unit.id}.explain.watch`),
		uid: uid(),
	};
}

register<ExplainStep, string>({
	id: "explain",
	appliesTo: ["form"],
	// The drawn item is ignored: the card is about the whole paradigm, which is
	// why BuildContext carries the unit.
	build: (ctx: BuildContext) => makeExplain(ctx.unit, ctx.course),
	grade: () => true,
	graded: false,
	byEar: () => false,
	heard: () => false,
	// Reading a card earns no key. The compose steps do that work, and the unit's
	// key set still covers all six forms because unitKeys derives from which
	// items a `draw: "teaches"` step *may* draw, not from what keys() returns.
	keys: () => [],
	check(step) {
		const out: string[] = [];
		if (step.rows.length === 0) out.push("has no paradigm rows");
		const leads = step.rows.filter((r) => r.lead !== null).length;
		if (leads !== 0 && leads !== step.rows.length)
			out.push("has a leading column on only some rows");
		if (step.rows.some((r) => r.gloss.length === 0)) out.push("has a row with no gloss");
		return out;
	},
});
